<template>
    <div>
        <transition name="fade" mode="out-in">
            
            <div
                v-if="!todos.length && loadingList"
                key="initialLoadingList"
            >
                <busy-spinner inner-class="my-5" text="Loading response items..."></busy-spinner>
            </div>

            <div 
                v-else
                key="initialLoaded"
                :class="loadingList ? 'opacity-50' : ''"
            >  
                <!-- LIST -->
                <div class="d-flex gap-3 align-items-baseline">
                    <h6>Response Items</h6>
                    <span class="mb-0 text-muted small">
                        This is a preview of the items you will be responding to in the "Your Response" section.
                    </span>
                </div>
                
                <div role="tablist">
                    <div
                        v-for="todo in todos"
                        :key="todo.id"
                        class="mb-4 border rounded bg-white shadow-sm overflow-hidden"
                    >
                        <div 
                            header-tag="header"
                            role="tab"
                            class="w-100 rounded-0 bg-gray-50"
                        >
                            <div
                                block
                                size="lg"
                                variant="bare-secondary"
                                role="button"
                                class="rctd-preview-button d-flex justify-content-start align-items-stretch p-0"
                                @click="toggleTodo(todo.id)"
                            > <!-- can add directive for toggle:  v-b-toggle="`rctd-${todo.id}`"  -->
                                <div 
                                    class="d-flex justify-content-center align-items-center px-2 py-2 bg-primary-50 min-width-100 max-width-100"
                                    style="border-left: 4px solid var(--primary);"
                                >
                                    <div class="flex-grow-1 text-center text-primary-600">
                                        <b-icon v-if="todo" :icon="todo.categoryType.icon || 'gear'" font-scale="1.2" class="mt-1"></b-icon>
                                        <div class="font-size-60 mt-1 text-uppercase">{{ todo.categoryType.title }}</div>
                                    </div>
                                </div>
                                <div class="flex-fill d-flex flex-wrap justify-content-between align-items-center py-2">
                                    <div class="w-50 px-3 max-width-400 d-flex justify-content-between align-items-center">
                                        <div class="w-100 d-flex flex-column justify-content-center">
                                            <div class="w-100 d-flex justify-content-between align-items-baseline">
                                                <h5 class="mb-0">{{ todo.title }}</h5>
                                            </div>
                                            <p v-if="todo.final_instructions" class="mb-0 mt-2 text-muted small">{{ todo.final_instructions }}</p>
                                        </div>
                                        <div>
                                            <span class="h6 mb-0 ml-3 text-uppercase font-weight-600 font-size-70" :class="todo.required ? 'text-black' : 'text-black-50'">
                                                {{ todo.required ? 'Required' : 'Optional' }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="pr-3">
                                        <div v-if="todoVisibilityById[todo.id]" class="d-flex align-items-center gap-3">
                                            <span class="text-uppercase font-weight-500 font-size-70 text-muted">Hide Details</span>
                                            <b-icon icon="dash-circle" font-scale="1.5"></b-icon>
                                        </div>
                                        <div v-else class="d-flex align-items-center gap-3">
                                            <span class="text-uppercase font-weight-500 font-size-70 text-muted">Show Details</span>
                                            <b-icon icon="plus-circle" font-scale="1.5"></b-icon>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <b-collapse 
                            :id="`rctd-${todo.id}`" 
                            role="tabpanel"
                            :visible="todoVisibilityById[todo.id]"
                        >
                            <div class="p-3 border-top">
                                <div v-if="todo.category === 'quotesheet'" class="">
                                    <QuoteSheetSupplierPreview
                                        :qsData="getLinkedModelData(todo.category, todo.linked_model_id)"
                                    ></QuoteSheetSupplierPreview>
                                </div>
                                <div v-else-if="todo.category === 'docrequest'" class="">
                                    <div v-if="todo.subcategory === 'upload'" class="w-100 min-height-50 d-flex align-items-center justify-content-center">
                                        <div class="font-size-80 text-muted font-italic">You will be prompted to upload your file once you start your response.</div>
                                    </div>
                                    <div v-if="todo.subcategory === 'template'" class="w-100 d-flex flex-column align-items-center justify-content-center text-center">
                                        <div class="font-size-80 text-muted font-italic">You will be prompted to upload your completed file once you start your response.</div>
                                        <div v-if="getLinkedModelData(todo.category, todo.linked_model_id)" class="mt-3 w-100 min-height-50 d-flex gap-3 align-items-center justify-content-center">
                                            <div class="text-muted text-uppercase font-size-80">Download<br>the file:</div>
                                            <base-s-3-file-download-button
                                                :fileData="getLinkedModelData(todo.category, todo.linked_model_id).template_file"
                                                button-variant="subtle-primary"
                                            ></base-s-3-file-download-button>
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="todo.category === 'formium_form'" class="w-100">
                                    <div class="font-size-80 text-muted font-italic">
                                        You will be prompted to answer these questions once you start your response:
                                    </div>
                                    <b-table
                                        :fields="formiumFormPreviewFields"
                                        :items="getLinkedModelData(todo.category, todo.linked_model_id).active_items"
                                        small
                                        hover
                                        class="mt-3"
                                    ></b-table>
                                </div>
                                <div v-else-if="todo.category === 'smartform'" class="w-100 min-height-50 d-flex align-items-center justify-content-center">
                                    <div class="font-size-80 text-muted font-italic">You can view the details once you start your response.</div>
                                </div>
                                <div v-else class="w-100 min-height-50 d-flex align-items-center justify-content-center">
                                    <div class="font-size-80 text-muted font-italic">Preview not available</div>
                                </div>
                            </div>
                        </b-collapse>
                    </div>
                </div>

            </div>
        </transition>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import QuoteSheetSupplierPreview from '../quotesheets/QuoteSheetSupplierPreview.vue';
import { getCompanyToDoTypeByCategory } from '../company_todos/company_todo_types';
import BaseS3FileDownloadButton from '../s3_files/BaseS3FileDownloadButton.vue';
// TODO - make/import preview components for all other todo categories - based on the reply viewers


export default {
    name: 'PortalRfxTodoPreviewList',
    components: { 
        QuoteSheetSupplierPreview,
        BaseS3FileDownloadButton,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        rfxId: {  
            type: String,
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingList: false,
            todos: [],  // instances of class RfxCompanyToDoReplyStatus
            todoVisibilityById: {},  // store the visibility of each todo by ID
            quoteSheetsById: {},
            formiumFormsById: {},
            docRequestsById: {},
            smartFormsById: {},
            //
            formiumFormPreviewFields: [
                { key: 'label', label: 'Question', sortable: false },
                { key: 'required', label: 'Required?', sortable: false, formatter: value => value ? 'Yes' : 'No'},
            ],
        }
    },
    computed: {

    },
    mounted: function() {
        this.getList();
    },
    methods: {
        // use apiErrorHandler(error) to handle most API errors and show toasts
        async getList() {
            this.loadingList = true;
            let resp;
            try {
                resp = await suppliosApi.rfx.list_rfx_company_todos_external_preview(this.rfxId);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.todos = resp.data.rfx_company_todos.map(t => {
                    // add some extra properties to each todo
                    t.categoryType = getCompanyToDoTypeByCategory(t.category, t.subcategory);
                    return t;
                });
                // set initial visibility
                const categoriesInitiallyVisible = ['quotesheet', 'formium_form'];  // not 'smartform' or 'docrequest' by default
                this.todoVisibilityById = Object.fromEntries(this.todos.map(todo => [todo.id, categoriesInitiallyVisible.includes(todo.category) ? true : false]));
                /// stash all the quotesheets, docrequests, formium forms, etc. in separate arrays for easier access
                const quoteSheets = resp.data.quotesheets || [];
                const smartForms = resp.data.smartforms || [];
                const docRequests = resp.data.docrequests || [];
                const formiumForms = resp.data.formium_forms || [];
                // stash them by ID for easier access
                this.quoteSheetsById = Object.fromEntries(quoteSheets.map(qs => [qs.id, qs]));
                this.smartFormsById = Object.fromEntries(smartForms.map(sf => [sf.id, sf]));
                this.docRequestsById = Object.fromEntries(docRequests.map(dr => [dr.id, dr]));
                this.formiumFormsById = Object.fromEntries(formiumForms.map(ff => [ff.id, ff]));
            }
            this.loadingList = false;
        },
        getLinkedModelData(category, linkedModelId) {
            // get the data for a linked model (quotesheet, docrequest, formium_form, smartform, etc.)
            // category is the category of the linked model
            // linkedModelId is the ID of the linked model
            switch (category) {
                case 'quotesheet':
                    return this.quoteSheetsById[linkedModelId];
                case 'docrequest':
                    return this.docRequestsById[linkedModelId];
                case 'formium_form':
                    return this.formiumFormsById[linkedModelId];
                case 'smartform':
                    return this.smartFormsById[linkedModelId];
                default:
                    return null;
            }
        },
        toggleTodo(todoId) {
            // toggle the visibility of a todo by ID
            console.log('toggling visibility for todo ID: ', todoId);
            const currentVisibility = this.todoVisibilityById[todoId];
            this.$set(this.todoVisibilityById, todoId, !currentVisibility);  // set it in reactive way
        },
    }
}
</script>

<style scoped lang="scss">

.rctd-preview-button {
    &:hover {
        background-color: var(--gray-100);
    }
}

</style>